import type { Role, TeamMfaPolicy, TeamResponse } from '@platform/contracts';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Monogram,
  EmptyState,
  Label,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@platform/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MailPlus, ShieldAlert, ShieldCheck, Trash2, UserPlus, UsersRound } from 'lucide-react';
import { useId, useState } from 'react';
import { toast } from 'sonner';

import { Field, FormError, fd } from '../components/form';
import { Guard, PageHeader } from '../components/page';
import { Loadable } from '../components/states';
import { api, errorMessage } from '../lib/api';
import { fmtAgo, fmtDate, ROLE_LABELS } from '../lib/format';

function InviteForm({ team }: { team: TeamResponse }) {
  const qc = useQueryClient();
  const scopeId = useId();
  const roleId = useId();
  const [target, setTarget] = useState(0);
  const scope = team.scopes[target] ?? team.scopes[0];
  const [role, setRole] = useState<Role | ''>('');
  const effectiveRole =
    scope && role && scope.roles.includes(role) ? role : (scope?.roles.at(-1) ?? '');
  const invite = useMutation({
    mutationFn: (body: unknown) => api('/api/invitations', { method: 'POST', body }),
    onSuccess: () => {
      toast.success('Invitación enviada');
      void qc.invalidateQueries({ queryKey: ['team'] });
    },
  });
  return (
    <form
      className="grid gap-4 md:grid-cols-4 md:items-end"
      onSubmit={(e) => {
        e.preventDefault();
        if (!scope) return;
        const form = e.currentTarget;
        invite.mutate(
          {
            email: fd(new FormData(form), 'email'),
            scopeType: scope.scopeType,
            scopeId: scope.scopeId,
            role: effectiveRole,
          },
          {
            onSuccess: () => {
              form.reset();
            },
          },
        );
      }}
    >
      <Field label="Email" name="email" type="email" required />
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={scopeId}>A dónde</Label>
        <select
          id={scopeId}
          value={target}
          onChange={(e) => {
            setTarget(Number(e.target.value));
          }}
          className="h-9 rounded-md border border-input bg-card px-2 text-sm"
        >
          {team.scopes.map((s, i) => (
            <option key={`${s.scopeType}:${s.scopeId}`} value={i}>
              {s.label}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={roleId}>Rol</Label>
        <select
          id={roleId}
          value={effectiveRole}
          onChange={(e) => {
            setRole(e.target.value as Role);
          }}
          className="h-9 rounded-md border border-input bg-card px-2 text-sm"
        >
          {scope?.roles.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABELS[r] ?? r}
            </option>
          ))}
        </select>
      </div>
      <Button type="submit" disabled={invite.isPending || !scope}>
        <UserPlus /> Invitar
      </Button>
      {/* Sólo ocupa lugar cuando hay error (role="alert" se anuncia al aparecer). */}
      {invite.error ? (
        <div className="md:col-span-4">
          <FormError message={errorMessage(invite.error)} />
        </div>
      ) : null}
    </form>
  );
}

/** "Exigir verificación en dos pasos a todo el equipo", por cliente. */
function MfaPolicyRow({ policy }: { policy: TeamMfaPolicy }) {
  const qc = useQueryClient();
  const id = useId();
  const update = useMutation({
    mutationFn: (requireMfa: boolean) =>
      api(`/api/team/clients/${policy.clientId}/mfa-policy`, {
        method: 'PUT',
        body: { requireMfa },
      }),
    onSuccess: (_, requireMfa) => {
      toast.success(
        requireMfa
          ? 'Hecho: todo el equipo tendrá que usar la verificación en dos pasos'
          : 'La verificación en dos pasos vuelve a ser opcional',
      );
      void qc.invalidateQueries({ queryKey: ['team'] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const pending = policy.membersWithoutMfa;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-start gap-3">
        <Switch
          id={id}
          checked={policy.requireMfa}
          disabled={update.isPending}
          onCheckedChange={(v) => {
            if (
              v &&
              pending > 0 &&
              !confirm(
                `${String(pending)} ${pending === 1 ? 'persona' : 'personas'} de ${policy.clientName} todavía no ${pending === 1 ? 'la tiene' : 'la tienen'}: en su próximo paso por el panel tendrán que activarla antes de seguir. ¿Continuar?`,
              )
            ) {
              return;
            }
            update.mutate(v);
          }}
          className="mt-0.5"
        />
        <div className="flex flex-col gap-0.5">
          <Label htmlFor={id}>
            Exigir verificación en dos pasos a todo el equipo
            {/* Varios clientes: se aclara a cuál aplica. */}
            <span className="font-normal text-muted-foreground"> · {policy.clientName}</span>
          </Label>
          <p className="text-sm text-muted-foreground">
            Si la activas, nadie de {policy.clientName} podrá entrar sin el código de su móvil ni
            desactivarla. Quien todavía no la tenga tendrá que configurarla al entrar.
          </p>
          <p className="text-xs text-muted-foreground">
            {pending === 0
              ? `Todo el equipo (${String(policy.membersTotal)}) ya la tiene activada.`
              : `${String(pending)} de ${String(policy.membersTotal)} ${policy.membersTotal === 1 ? 'persona' : 'personas'} sin verificación en dos pasos.`}
          </p>
        </div>
      </div>
    </div>
  );
}

export function TeamPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['team'], queryFn: () => api<TeamResponse>('/api/team') });
  const refresh = () => qc.invalidateQueries({ queryKey: ['team'] });
  const onError = (err: unknown) => toast.error(errorMessage(err));
  const changeRole = useMutation({
    mutationFn: (v: { id: string; role: Role }) =>
      api(`/api/team/memberships/${v.id}`, { method: 'PATCH', body: { role: v.role } }),
    onSuccess: () => {
      toast.success('Rol actualizado');
      void refresh();
    },
    onError,
  });
  const removeMember = useMutation({
    mutationFn: (id: string) => api(`/api/team/memberships/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Acceso quitado');
      void refresh();
    },
    onError,
  });
  const resend = useMutation({
    mutationFn: (id: string) => api(`/api/invitations/${id}/resend`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('Invitación reenviada (el link anterior deja de servir)');
      void refresh();
    },
    onError,
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/api/invitations/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Invitación revocada');
      void refresh();
    },
    onError,
  });
  const remind = useMutation({
    mutationFn: (userId: string) => api(`/api/team/users/${userId}/remind-2fa`, { method: 'POST' }),
    onSuccess: () => toast.success('Recordatorio de 2FA enviado'),
    onError,
  });
  const team = q.data;
  return (
    <Guard requires="users:invite">
      <PageHeader
        title="Equipo"
        description="Quién entra al panel y qué puede hacer. Las invitaciones caducan a los 7 días."
      />
      <Loadable isLoading={q.isLoading} error={q.error} onRetry={() => void q.refetch()}>
        {team ? (
          <div className="flex flex-col gap-6">
            <Card>
              <CardHeader>
                <CardTitle icon={<UserPlus />}>Invitar a alguien</CardTitle>
              </CardHeader>
              <CardContent>
                <InviteForm team={team} />
              </CardContent>
            </Card>
            {team.mfaPolicies && team.mfaPolicies.length > 0 ? (
              <Card>
                <CardHeader>
                  <CardTitle icon={<ShieldCheck />}>Seguridad del equipo</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-4">
                  {team.mfaPolicies.map((p) => (
                    <MfaPolicyRow key={p.clientId} policy={p} />
                  ))}
                </CardContent>
              </Card>
            ) : null}
            <Card>
              <CardHeader>
                <CardTitle icon={<UsersRound />}>Personas</CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto p-0">
                {team.members.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground">Todavía no hay nadie.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Persona</TableHead>
                        <TableHead>Acceso</TableHead>
                        <TableHead className="hidden md:table-cell">Último ingreso</TableHead>
                        <TableHead className="w-12" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {team.members.map((m) => (
                        <TableRow key={m.userId}>
                          <TableCell>
                            <div className="flex items-start gap-3">
                              <Monogram
                                name={m.name}
                                colorKey={m.email}
                                src={m.avatarUrl}
                                kind="photo"
                                size="sm"
                                className="mt-0.5 rounded-full"
                              />
                              <div>
                                <p className="font-medium">{m.name}</p>
                                <p className="text-xs text-muted-foreground">{m.email}</p>
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {m.status === 'disabled' ? (
                                    <Badge variant="secondary">deshabilitado</Badge>
                                  ) : null}
                                  {m.totpEnabled ? (
                                    <Badge variant="success">2FA</Badge>
                                  ) : (
                                    <Badge variant="warning">sin 2FA</Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <ul className="flex flex-col gap-1">
                              {m.memberships.map((ms) => {
                                const scope = team.scopes.find(
                                  (s) => s.scopeType === ms.scopeType && s.scopeId === ms.scopeId,
                                );
                                return (
                                  <li
                                    key={ms.id}
                                    className="flex flex-wrap items-center gap-2 text-sm"
                                  >
                                    <span className="text-muted-foreground">{ms.scopeName}:</span>
                                    {ms.manageable && scope ? (
                                      <select
                                        aria-label={`Rol de ${m.email} en ${ms.scopeName}`}
                                        value={ms.role}
                                        onChange={(e) => {
                                          changeRole.mutate({
                                            id: ms.id,
                                            role: e.target.value as Role,
                                          });
                                        }}
                                        className="h-8 rounded-md border border-input bg-card px-2 text-xs"
                                      >
                                        {scope.roles.map((r) => (
                                          <option key={r} value={r}>
                                            {ROLE_LABELS[r] ?? r}
                                          </option>
                                        ))}
                                      </select>
                                    ) : (
                                      <Badge variant="outline">
                                        {ROLE_LABELS[ms.role] ?? ms.role}
                                      </Badge>
                                    )}
                                    {ms.manageable ? (
                                      <Button
                                        size="icon"
                                        variant="ghost"
                                        aria-label={`Quitar a ${m.email} de ${ms.scopeName}`}
                                        onClick={() => {
                                          if (
                                            confirm(
                                              `¿Quitarle a ${m.email} el acceso a ${ms.scopeName}?`,
                                            )
                                          ) {
                                            removeMember.mutate(ms.id);
                                          }
                                        }}
                                      >
                                        <Trash2 />
                                      </Button>
                                    ) : null}
                                  </li>
                                );
                              })}
                            </ul>
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            {fmtAgo(m.lastLoginAt)}
                          </TableCell>
                          <TableCell>
                            {!m.totpEnabled && m.status === 'active' ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  remind.mutate(m.userId);
                                }}
                                disabled={remind.isPending}
                              >
                                <ShieldAlert /> Recordar 2FA
                              </Button>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle icon={<MailPlus />}>Invitaciones pendientes</CardTitle>
              </CardHeader>
              <CardContent>
                {team.invitations.length === 0 ? (
                  <EmptyState icon={<MailPlus />} title="No hay invitaciones pendientes" />
                ) : (
                  <ul className="flex flex-col gap-2">
                    {team.invitations.map((i) => (
                      <li
                        key={i.id}
                        className="flex flex-wrap items-center gap-2 rounded-md border p-2 text-sm"
                      >
                        <span className="font-medium">{i.email}</span>
                        <Badge variant="outline">{ROLE_LABELS[i.role] ?? i.role}</Badge>
                        <span className="text-xs text-muted-foreground">
                          {i.scopeName} · {i.expired ? 'vencida' : `vence ${fmtDate(i.expiresAt)}`}
                          {i.invitedByEmail ? ` · invitó ${i.invitedByEmail}` : ''}
                        </span>
                        <div className="ml-auto flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              resend.mutate(i.id);
                            }}
                            disabled={resend.isPending}
                          >
                            Reenviar
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              revoke.mutate(i.id);
                            }}
                            disabled={revoke.isPending}
                          >
                            Revocar
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        ) : null}
      </Loadable>
    </Guard>
  );
}
